import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { RootState } from "../../common/state/store";
import { getCurrentUser } from "../../features/profile/state/profileSlice";
import {
  createReport,
  deleteReport,
  fetchReports,
  reportByIdSelector,
  reportsIsSubmittingSelector,
  reportsIsSendingPreviewSelector,
  reportsLastPreviewSelector,
  clearLastPreview,
  sendPreviewReport,
  updateReport,
} from "../../features/reports/state/reportsSlice";
import { setAlert } from "../../features/alert/state/alertsSlice";
import type {
  Cadence,
  ReportLanguage,
  ScopeType,
  ScheduledEmailReportInput,
} from "../../features/reports/types";
import ScopePicker from "../../features/reports/components/ScopePicker";
import CadenceSelector from "../../features/reports/components/CadenceSelector";
import RecipientsComposer from "../../features/reports/components/RecipientsComposer";
import StatusBadge from "../../features/reports/components/StatusBadge";
import EnabledBadge from "../../features/reports/components/EnabledBadge";
import PeriodPicker from "../../features/reports/components/PeriodPicker";
import RowActionsMenu from "../../features/reports/components/RowActionsMenu";
import ModalConfirmation from "../../common/components/Modal/ModalConfirmation";
import {
  openConfirmationModal,
  closeConfirmationModal,
} from "../../common/state/slice/modal/modalSlice";
import { trackEvent } from "../../trackEvent";

interface FormState {
  name: string;
  scope: { scope_type: ScopeType; scope_id: number } | null;
  cadence: Cadence;
  recipients: string[];
  include_learner_names: boolean;
  language: ReportLanguage;
}

// `enabled` is intentionally NOT part of the form state — toggling activation
// is a side action handled by the header kebab menu, which persists
// immediately. Keeping it out of the form prevents three things:
//  - duplicate display of the same status (header badge + form toggle)
//  - the "I unchecked Actif but didn't save" trap
//  - silent overwrite of an externally toggled state when the user saves an
//    unrelated edit
const emptyForm: FormState = {
  name: "",
  scope: null,
  cadence: "monthly",
  recipients: [],
  include_learner_names: false,
  language: "fr",
};

interface ReportsFormViewProps {
  mode: "create" | "edit";
}

// Shared id for the redux-driven confirmation modal. Following the same
// `<feature>-<action>-confirmation` naming convention as
// `unassign-rag-document-confirmation` etc.
const DELETE_REPORT_MODAL_ID = "delete-scheduled-email-report-confirmation";

const formatDateTime = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/**
 * Human-friendly relative time ("il y a 3 jours", "dans 2 heures"). Uses
 * `date-fns/formatDistanceToNow` with the user's UI locale — same pattern
 * already in use in `EnduserEngagementCard.tsx` so the wording matches the
 * rest of the stats module.
 */
const formatRelative = (iso: string | null | undefined, lang: string): string | null => {
  if (!iso) return null;
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: lang === "fr" ? fr : undefined,
    });
  } catch {
    return null;
  }
};

const formatPeriod = (start?: string, end?: string): string => {
  if (!start || !end) return "";
  try {
    const s = new Date(start).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const e = new Date(end).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `${s} → ${e}`;
  } catch {
    return "";
  }
};

/**
 * Section block inside the main form card. Header uses the same typographic
 * treatment as table column headers throughout StatsNew (`text-xs uppercase
 * tracking-wide text-gray-500 font-semibold`) so it sits visually flat into
 * the design system instead of competing as its own card.
 */
const FormSection = ({
  title,
  children,
  divider = true,
  heavyDivider = false,
  titleAccessory,
}: {
  title: string;
  children: ReactNode;
  divider?: boolean;
  heavyDivider?: boolean;
  titleAccessory?: ReactNode;
}) => {
  const dividerClass = !divider
    ? ""
    : heavyDivider
    ? "border-t-2 border-gray-200 pt-6 mt-6"
    : "border-t border-gray-100 pt-5 mt-5";
  return (
    <section className={dividerClass}>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
        {titleAccessory}
      </div>
      {children}
    </section>
  );
};

const ReportsFormView = ({ mode }: ReportsFormViewProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams<{ id?: string }>();
  const reportId = params.id ? Number(params.id) : null;
  const existing = useAppSelector(reportByIdSelector(reportId));
  const isSubmitting = useAppSelector(reportsIsSubmittingSelector);
  const isSendingPreviewId = useAppSelector(reportsIsSendingPreviewSelector);
  const lastPreview = useAppSelector(reportsLastPreviewSelector);
  const userEmail = useAppSelector((state: RootState) => getCurrentUser(state)?.email ?? "");

  const [form, setForm] = useState<FormState>(emptyForm);
  const [testRecipient, setTestRecipient] = useState<string>("");
  const [testPeriod, setTestPeriod] = useState<{
    startDate: string;
    endDate: string;
  } | null>(null);
  // Client-side cooldown after a Test send. The backend rate-limits at 5/min
  // (sendPreview.js), so a 15s gate here keeps spammy clicks from ever
  // tripping the 429. Resets on cadence change so the user isn't trapped
  // after editing the form.
  const TEST_SEND_COOLDOWN_MS = 15_000;
  const [testCooldownUntil, setTestCooldownUntil] = useState<number>(0);
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (testCooldownUntil <= Date.now()) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(tick);
  }, [testCooldownUntil]);
  const cooldownRemainingMs = Math.max(0, testCooldownUntil - now);
  const cooldownRemainingSec = Math.ceil(cooldownRemainingMs / 1000);

  useEffect(() => {
    // testRecipient is locked server-side to the caller's email (see
    // sendPreview.js security notes). Sync it whenever the current user's
    // email becomes known so the UI matches the backend's expectation.
    if (userEmail) setTestRecipient(userEmail);
  }, [userEmail]);

  useEffect(() => {
    if (!lastPreview) return;
    const msg = lastPreview.sent
      ? lastPreview.periodStart && lastPreview.periodEnd
        ? t("pages.statistics.reports.preview.successSentWithPeriod", {
            recipient: lastPreview.recipient,
            period: formatPeriod(lastPreview.periodStart, lastPreview.periodEnd),
          })
        : t("pages.statistics.reports.preview.successSent", {
            recipient: lastPreview.recipient,
          })
      : lastPreview.dryRun
      ? t("pages.statistics.reports.preview.dryRun", {
          reason: lastPreview.reason ?? "",
        })
      : t("pages.statistics.reports.preview.error");
    const type = lastPreview.sent ? "success" : lastPreview.dryRun ? "warning" : "danger";
    dispatch(setAlert({ type, msg, customTimeoutInMs: 6000 }));
    dispatch(clearLastPreview());
  }, [lastPreview, dispatch, t]);

  useEffect(() => {
    if (mode === "edit" && !existing) {
      void dispatch(fetchReports());
    }
  }, [mode, existing, dispatch]);

  useEffect(() => {
    if (mode === "edit" && existing) {
      setForm({
        name: existing.name,
        scope: { scope_type: existing.scope_type, scope_id: existing.scope_id },
        cadence: existing.cadence,
        recipients: existing.recipients ?? [],
        include_learner_names: existing.include_learner_names,
        language: existing.language ?? "fr",
      });
    }
  }, [mode, existing]);

  const failValidation = (msg: string): null => {
    dispatch(setAlert({ type: "danger", msg, customTimeoutInMs: 6000 }));
    return null;
  };

  const buildInput = (): ScheduledEmailReportInput | null => {
    if (!form.name.trim()) {
      return failValidation(t("pages.statistics.reports.validation.nameRequired"));
    }
    if (!form.scope) {
      return failValidation(t("pages.statistics.reports.validation.scopeRequired"));
    }
    if (form.recipients.length === 0) {
      return failValidation(t("pages.statistics.reports.validation.recipientsRequired"));
    }
    return {
      name: form.name.trim(),
      scope_type: form.scope.scope_type,
      scope_id: form.scope.scope_id,
      cadence: form.cadence,
      recipients: form.recipients,
      include_learner_names: form.include_learner_names,
      language: form.language,
    };
  };

  const handleSave = async (thenNavigate: boolean) => {
    const input = buildInput();
    if (!input) return null;
    try {
      if (mode === "create") {
        trackEvent("report-create");
        const created = await dispatch(createReport(input)).unwrap();
        dispatch(
          setAlert({
            type: "success",
            msg: t("pages.statistics.reports.toast.created"),
            customTimeoutInMs: 4000,
          }),
        );
        if (thenNavigate) navigate("/statistics/reports");
        return created.id;
      }
      if (reportId) {
        await dispatch(updateReport({ id: reportId, input })).unwrap();
        dispatch(
          setAlert({
            type: "success",
            msg: t("pages.statistics.reports.toast.updated"),
            customTimeoutInMs: 4000,
          }),
        );
        if (thenNavigate) navigate("/statistics/reports");
        return reportId;
      }
      return null;
    } catch {
      dispatch(
        setAlert({
          type: "danger",
          msg: t("pages.statistics.reports.toast.saveError"),
          customTimeoutInMs: 6000,
        }),
      );
      return null;
    }
  };

  // Header kebab actions — both persist immediately, so the user is never in
  // an "I toggled but didn't save" state. The form's other fields stay
  // independent of this side-action.
  const handleToggleEnabled = async () => {
    if (!reportId || !existing) return;
    try {
      await dispatch(
        updateReport({ id: reportId, input: { enabled: !existing.enabled } }),
      ).unwrap();
      dispatch(
        setAlert({
          type: "success",
          msg: t(
            existing.enabled
              ? "pages.statistics.reports.toast.disabled"
              : "pages.statistics.reports.toast.enabled",
          ),
          customTimeoutInMs: 3000,
        }),
      );
    } catch {
      dispatch(
        setAlert({
          type: "danger",
          msg: t("pages.statistics.reports.toast.saveError"),
          customTimeoutInMs: 6000,
        }),
      );
    }
  };

  const handleDeleteRequest = () => {
    if (!reportId) return;
    dispatch(openConfirmationModal(DELETE_REPORT_MODAL_ID));
  };

  const handleDeleteConfirm = async () => {
    if (!reportId) return;
    dispatch(closeConfirmationModal(DELETE_REPORT_MODAL_ID));
    try {
      await dispatch(deleteReport(reportId)).unwrap();
      dispatch(
        setAlert({
          type: "success",
          msg: t("pages.statistics.reports.toast.deleted"),
          customTimeoutInMs: 4000,
        }),
      );
      navigate("/statistics/reports");
    } catch {
      dispatch(
        setAlert({
          type: "danger",
          msg: t("pages.statistics.reports.toast.deleteError"),
          customTimeoutInMs: 6000,
        }),
      );
    }
  };

  const handleSendTest = async () => {
    if (!reportId) return;
    if (!testPeriod) return;
    if (!testRecipient.trim()) return;
    if (cooldownRemainingMs > 0) return;
    try {
      await dispatch(
        sendPreviewReport({
          id: reportId,
          startDate: testPeriod.startDate,
          endDate: testPeriod.endDate,
          testRecipient: testRecipient.trim(),
        }),
      ).unwrap();
      // Success toast is dispatched by the lastPreview effect — nothing to do.
    } catch (err) {
      const rejectPayload = err as { status?: number | null; message?: string } | undefined;
      const status = rejectPayload?.status ?? null;
      const msg =
        status === 429
          ? t("pages.statistics.reports.preview.rateLimited")
          : t("pages.statistics.reports.preview.error");
      dispatch(setAlert({ type: "danger", msg, customTimeoutInMs: 6000 }));
    } finally {
      // Arm the cooldown whether the send succeeded or not — a 429/validation
      // error is still a "you tried to send", and we want to dampen retries.
      setTestCooldownUntil(Date.now() + TEST_SEND_COOLDOWN_MS);
      setNow(Date.now());
    }
  };

  const subtitle =
    mode === "edit" && existing
      ? t("pages.statistics.reports.editSubtitle", {
          name: existing.name,
          cadence: t(`pages.statistics.reports.cadence.${existing.cadence}`),
          count: existing.recipients?.length ?? 0,
        })
      : null;

  const lastRunLabel = formatDateTime(existing?.last_run_at);
  const nextRunLabel = formatDateTime(existing?.next_run_at);
  const lastRunRelative = formatRelative(existing?.last_run_at, i18n.language);
  const nextRunRelative = formatRelative(existing?.next_run_at, i18n.language);
  // Backend disables a report after MAX_CONSECUTIVE_FAILURES (=6, see
  // processScheduledReports.js). Surface the counter once it starts climbing
  // so the Creator can act before the auto-disable hits, instead of being
  // surprised when the badge silently flips to "Désactivé".
  const failureCount = Number(existing?.consecutive_failures ?? 0);
  const MAX_FAILURES_BEFORE_AUTO_DISABLE = 6;

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb */}
      <nav className="stats-breadcrumb mb-2 flex-shrink-0">
        <Link to="/statistics/reports">{t("pages.statistics.reports.breadcrumb")}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <span className="stats-breadcrumb-current">
          {mode === "create"
            ? t("pages.statistics.reports.new")
            : t("pages.statistics.reports.editTitle")}
        </span>
      </nav>

      {/* Header — same pattern as ApprenantDetailView user header.
          The Active/Disabled state lives here as the SINGLE canonical
          display + action (via kebab menu). We deliberately removed the
          "État" form section to avoid the three-times-Actif redundancy
          the old layout had. */}
      <div className="flex-shrink-0 bg-white rounded-lg border border-gray-200 px-4 py-2 mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-gray-900 truncate">
            {mode === "create"
              ? t("pages.statistics.reports.new")
              : t("pages.statistics.reports.editTitle")}
          </h2>
          {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
        </div>
        {mode === "edit" && existing && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <EnabledBadge enabled={existing.enabled} />
            <RowActionsMenu
              enabled={existing.enabled}
              onToggleEnabled={handleToggleEnabled}
              onDelete={handleDeleteRequest}
            />
          </div>
        )}
      </div>

      {/* Scrollable content area — fills the remaining viewport height */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3 items-start">
          {/* MAIN form card — single container, all sections inside */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <FormSection title={t("pages.statistics.reports.sections.identity")} divider={false}>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                {t("pages.statistics.reports.fields.name")}
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("pages.statistics.reports.fields.namePlaceholder")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                maxLength={120}
              />
            </FormSection>

            <FormSection title={t("pages.statistics.reports.sections.scope")}>
              <ScopePicker
                value={form.scope}
                onChange={(next) => setForm({ ...form, scope: next })}
              />
            </FormSection>

            <FormSection title={t("pages.statistics.reports.sections.schedule")}>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="max-w-3xl">
                    <div className="text-sm font-medium text-gray-700">
                      {t("pages.statistics.reports.cadence.fieldLabel")}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t(`pages.statistics.reports.cadence.help.${form.cadence}`)}
                    </div>
                  </div>
                  <CadenceSelector
                    value={form.cadence}
                    onChange={(next) => setForm({ ...form, cadence: next })}
                  />
                </div>
                {/* Language selector hidden for now — backend still honors the
                    `language` field, but the report is always created in FR
                    until the EN SendGrid template ships. Re-enable by
                    restoring the LanguageSelector block here. */}
              </div>
            </FormSection>

            <FormSection title={t("pages.statistics.reports.sections.recipients")}>
              <RecipientsComposer
                value={form.recipients}
                onChange={(next) => setForm({ ...form, recipients: next })}
              />
            </FormSection>

            <FormSection title={t("pages.statistics.reports.sections.confidentiality")}>
              <div className="flex items-center justify-between gap-4">
                <div className="max-w-3xl">
                  <div className="text-sm font-medium text-gray-700">
                    {t("pages.statistics.reports.fields.includeLearnerNames")}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t("pages.statistics.reports.fields.includeLearnerNamesHelp")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      include_learner_names: !form.include_learner_names,
                    })
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition flex-shrink-0 ${
                    form.include_learner_names ? "bg-green-500" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      form.include_learner_names ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </FormSection>
          </div>

          {/* SIDEBAR — Schedule status (edit only) + Test send panel (always). */}
          <div className="flex flex-col gap-3 min-w-0">
            {/* Schedule card — only meaningful once the report has run.
                Layout: stacked rows with the relative time as the primary
                read, the absolute timestamp as small secondary text, and
                the StatusBadge inline. Mirrors EnduserEngagementCard's
                "X il y a Y" pattern so the stats module feels consistent. */}
            {mode === "edit" && existing && (
              <aside className="bg-white rounded-lg border border-gray-200 p-4">
                {/* Last run */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 pt-0.5">
                    {t("pages.statistics.reports.sidebar.lastRun")}
                  </span>
                  <div className="flex flex-col items-end gap-1 min-w-0">
                    {lastRunRelative ? (
                      <>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <span className="text-sm text-gray-900">{lastRunRelative}</span>
                          {existing.last_run_status && (
                            <StatusBadge status={existing.last_run_status} />
                          )}
                        </div>
                        {lastRunLabel && (
                          <span className="text-[11px] text-gray-400">{lastRunLabel}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">
                        {t("pages.statistics.reports.sidebar.neverSent")}
                      </span>
                    )}
                  </div>
                </div>

                {/* Consecutive failures alert — only when > 0 to keep the
                    card quiet at steady state. Color escalates as the counter
                    approaches the auto-disable threshold. */}
                {failureCount > 0 && (
                  <div
                    className={`text-xs rounded-md px-2.5 py-1.5 mb-3 border ${
                      failureCount >= MAX_FAILURES_BEFORE_AUTO_DISABLE - 1
                        ? "bg-red-50 border-red-200 text-red-800"
                        : "bg-amber-50 border-amber-100 text-amber-800"
                    }`}
                  >
                    {t("pages.statistics.reports.sidebar.consecutiveFailures", {
                      count: failureCount,
                      max: MAX_FAILURES_BEFORE_AUTO_DISABLE,
                    })}
                  </div>
                )}

                {/* Next run */}
                <div className="border-t border-gray-100 pt-3 flex items-start justify-between gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 pt-0.5">
                    {t("pages.statistics.reports.sidebar.nextRun")}
                  </span>
                  <div className="flex flex-col items-end gap-1 min-w-0">
                    {nextRunRelative ? (
                      <>
                        <span className="text-sm text-gray-900">{nextRunRelative}</span>
                        {nextRunLabel && (
                          <span className="text-[11px] text-gray-400">{nextRunLabel}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">
                        {t("pages.statistics.reports.sidebar.noNextRun")}
                      </span>
                    )}
                  </div>
                </div>
              </aside>
            )}

            {/* Test panel — visible in create mode too (disabled until the
                report is saved, so the affordance is discoverable from the
                first second the user lands on the form). */}
            <aside className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                {t("pages.statistics.reports.testPanel.title")}
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                {t("pages.statistics.reports.testPanel.description")}
              </p>

              {!reportId && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2.5 py-1.5 mb-3">
                  {t("pages.statistics.reports.testPanel.saveFirst")}
                </p>
              )}

              <label className="text-xs font-medium text-gray-700 block mb-1">
                {t("pages.statistics.reports.testPanel.recipientLabel")}
              </label>
              {/* Locked to the caller's own email — backend rejects any other
                  address (see sendPreview security notes). Showing it as a
                  readonly field with a clear helper avoids the silent 403
                  the editable input used to produce. */}
              <input
                type="email"
                value={testRecipient}
                readOnly
                aria-readonly
                title={t("pages.statistics.reports.testPanel.recipientLockedTooltip")}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 cursor-not-allowed"
              />
              <p className="text-[11px] text-gray-500 mt-1 mb-3">
                {t("pages.statistics.reports.testPanel.recipientLockedHelp")}
              </p>

              <label className="text-xs font-medium text-gray-700 block mb-1">
                {t(`pages.statistics.reports.testPanel.periodLabel.${form.cadence}`)}
              </label>
              <div className={!reportId ? "pointer-events-none opacity-50" : ""}>
                <PeriodPicker cadence={form.cadence} value={testPeriod} onChange={setTestPeriod} />
              </div>

              <button
                type="button"
                onClick={handleSendTest}
                disabled={
                  !reportId ||
                  !testRecipient.trim() ||
                  !testPeriod ||
                  isSendingPreviewId === reportId ||
                  cooldownRemainingMs > 0
                }
                className="btn-primary-outline w-full inline-flex items-center justify-center gap-2 mt-3"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                {isSendingPreviewId === reportId
                  ? t("pages.statistics.reports.actions.sending")
                  : cooldownRemainingMs > 0
                  ? t("pages.statistics.reports.testPanel.sendButtonCooldown", {
                      seconds: cooldownRemainingSec,
                    })
                  : t("pages.statistics.reports.testPanel.sendButton")}
              </button>
            </aside>
          </div>
        </div>
      </div>

      {/* Sticky footer — gray-100 bar with gray-300 top border, matching the
          canonical FooterButtons pattern but without a fixed height. Sits at
          the bottom of the flex column as a flex-shrink-0 sibling. */}
      <div className="flex-shrink-0 bg-gray-100 border-t border-gray-300 py-3 px-5 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate("/statistics/reports")}
          className="btn-alternative-outline"
        >
          {t("general.cancel")}
        </button>
        <button
          type="button"
          onClick={() => handleSave(true)}
          disabled={isSubmitting}
          className="btn-primary-fill"
        >
          {isSubmitting
            ? t("pages.statistics.reports.actions.saving")
            : mode === "create"
            ? t("pages.statistics.reports.actions.create")
            : t("pages.statistics.reports.actions.saveChanges")}
        </button>
      </div>

      {/* Delete confirmation — uses the app's canonical `ModalConfirmation`
          component (same one used to confirm parcours deletion, RAG doc
          unassign, etc.). Stays harmonised with the rest of the app's
          "destructive-but-routine" pattern. */}
      <ModalConfirmation
        id={DELETE_REPORT_MODAL_ID}
        title={t("pages.statistics.reports.deleteConfirm.title")}
        text={t("pages.statistics.reports.deleteConfirm.body")}
        submitButtonText={t("pages.statistics.reports.actions.delete")}
        onClickSubmit={handleDeleteConfirm}
      />
    </div>
  );
};

export default ReportsFormView;
